import React, { useState, useEffect, useCallback, useId, useMemo } from 'react'
import { Popover, IconButton, Box, Chip, CircularProgress } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import { getProfileLinkClass } from './coachDashboardStyles'

const InfoRow = ({ label, value, isDarkMode }) => {
  if (!value) {
    return null
  }

  return (
    <Box
      sx={{ display: 'flex', gap: 1, fontSize: '0.875rem', lineHeight: 1.4 }}
    >
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          fontWeight: 500,
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          minWidth: '5.5rem',
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          minWidth: 0,
          flex: 1,
          color: isDarkMode ? '#f3f4f6' : '#111827',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Box>
    </Box>
  )
}

const resolveCandidateId = (candidate, chatManager) => {
  if (candidate?.candidateId) {
    return candidate.candidateId
  }

  if (chatManager?.resolveCandidateId) {
    return chatManager.resolveCandidateId(candidate)
  }

  return null
}

const WaitingRoomCandidateInfoPopover = ({ candidate }) => {
  const { chatManager, isDarkMode, getDarkModeClasses } = useCoachChat()
  const { t } = useTranslation('coach_chat')
  const [anchorEl, setAnchorEl] = useState(null)
  const [candidateInfo, setCandidateInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const popoverId = useId()

  const chatToken = candidate?.username
  const open = Boolean(anchorEl)

  const loadCandidateInfo = useCallback(async () => {
    if (!chatToken || !chatManager) {
      setError('unavailable')
      setLoading(false)
      return
    }

    const cached = chatManager.candidateInfo?.[chatToken]
    const hasFullCache =
      cached && typeof chatManager._isFullCandidateInfo === 'function'
        ? chatManager._isFullCandidateInfo(cached)
        : Boolean(cached?.id)

    if (hasFullCache) {
      setCandidateInfo(cached)
      setError(null)
      setLoading(false)
      return
    }

    if (cached) {
      setCandidateInfo(cached)
    }

    setLoading(true)
    setError(null)

    try {
      const data = await chatManager.fetchCandidateInfo(chatToken)
      if (data) {
        setCandidateInfo(data)
      } else if (!cached) {
        setError('unavailable')
      }
    } catch {
      if (!cached) {
        setError('unavailable')
      }
    } finally {
      setLoading(false)
    }
  }, [chatToken, chatManager])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    loadCandidateInfo()

    const handleCandidateInfoFetched = (data) => {
      if (data.chatToken === chatToken) {
        setCandidateInfo(data.candidate)
        setError(null)
        setLoading(false)
      }
    }

    chatManager?.addEventListener(
      'candidateInfoFetched',
      handleCandidateInfoFetched,
    )

    return () => {
      chatManager?.removeEventListener(
        'candidateInfoFetched',
        handleCandidateInfoFetched,
      )
    }
  }, [open, chatToken, chatManager, loadCandidateInfo])

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const displayInfo = useMemo(() => {
    const presenceCandidateId = resolveCandidateId(candidate, chatManager)
    const name =
      candidateInfo?.name ||
      candidateInfo?.displayName ||
      candidate?.displayName ||
      candidate?.username

    return {
      name,
      email: candidateInfo?.email,
      company: candidateInfo?.company,
      product: candidateInfo?.product,
      programLevel:
        candidateInfo?.program_level || candidate?.programLevel || null,
      jobTitle: candidateInfo?.job_title,
      industry: candidateInfo?.industry,
      location: candidateInfo?.location,
      browser: candidate?.browser
        ? `${candidate.browser}${candidate?.isMobile ? ' (Mobile)' : ''}`
        : null,
      profileHref:
        candidateInfo?.candidate_path ||
        (candidateInfo?.id
          ? `/coach/candidates/${candidateInfo.id}`
          : presenceCandidateId
            ? `/coach/candidates/${presenceCandidateId}`
            : null),
    }
  }, [candidate, candidateInfo, chatManager])

  const textColor = isDarkMode ? '#f3f4f6' : '#111827'
  const chipColor = isDarkMode ? '#4b5563' : '#e5e7eb'
  const chipTextColor = isDarkMode ? '#e5e7eb' : '#374151'
  const iconColor = isDarkMode ? '#93c5fd' : '#08467c'

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={t(
          'coach_dashboard.candidate_info_aria',
          'View candidate info',
        )}
        aria-describedby={open ? popoverId : undefined}
        sx={{
          flexShrink: 0,
          color: iconColor,
          '&:hover': {
            backgroundColor: isDarkMode
              ? 'rgba(147, 197, 253, 0.1)'
              : 'rgba(8, 70, 124, 0.08)',
          },
        }}
      >
        <InfoOutlined sx={{ fontSize: 20 }} />
      </IconButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
            color: textColor,
            maxWidth: 420,
            width: 'min(420px, calc(100vw - 2rem))',
            p: 2,
            borderRadius: '10px',
          },
        }}
      >
        {loading && !candidateInfo ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : error && !displayInfo.name ? (
          <Box
            sx={{
              fontSize: '0.875rem',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
            }}
          >
            {t(
              'coach_dashboard.candidate_info_unavailable',
              'Candidate info not available',
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {displayInfo.name ? (
              <Box
                component="h4"
                sx={{
                  m: 0,
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: textColor,
                }}
              >
                {displayInfo.name}
              </Box>
            ) : null}
            {(displayInfo.product || displayInfo.programLevel) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {displayInfo.product ? (
                  <Chip
                    label={displayInfo.product}
                    size="small"
                    sx={{
                      backgroundColor: chipColor,
                      color: chipTextColor,
                      fontSize: '0.75rem',
                      height: '24px',
                    }}
                  />
                ) : null}
                {displayInfo.programLevel ? (
                  <Chip
                    label={displayInfo.programLevel}
                    size="small"
                    sx={{
                      backgroundColor: chipColor,
                      color: chipTextColor,
                      fontSize: '0.75rem',
                      height: '24px',
                    }}
                  />
                ) : null}
              </Box>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : null}
            {!loading &&
            (displayInfo.email ||
              displayInfo.company ||
              displayInfo.jobTitle ||
              displayInfo.industry ||
              displayInfo.location ||
              displayInfo.browser) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <InfoRow
                  label={t('coach_dashboard.candidate_info_email', 'Email')}
                  value={displayInfo.email}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label={t('coach_dashboard.candidate_info_company', 'Company')}
                  value={displayInfo.company}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label={t(
                    'coach_dashboard.candidate_info_job_title',
                    'Job title',
                  )}
                  value={displayInfo.jobTitle}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label={t(
                    'coach_dashboard.candidate_info_industry',
                    'Industry',
                  )}
                  value={displayInfo.industry}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label={t(
                    'coach_dashboard.candidate_info_location',
                    'Location',
                  )}
                  value={displayInfo.location}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label={t('coach_dashboard.candidate_info_browser', 'Browser')}
                  value={displayInfo.browser}
                  isDarkMode={isDarkMode}
                />
              </Box>
            ) : null}
            {displayInfo.profileHref ? (
              <a
                href={displayInfo.profileHref}
                target="_blank"
                rel="noopener noreferrer"
                className={getProfileLinkClass(getDarkModeClasses)}
              >
                {t('coach_dashboard.view_profile', 'View Profile')}
              </a>
            ) : null}
          </Box>
        )}
      </Popover>
    </>
  )
}

export default WaitingRoomCandidateInfoPopover
